/**
 * Shared agent model configuration.
 *
 * Single source of truth for per-agent default models and allowed override
 * models. Imported by both ai-bridge (server registration) and the admin
 * test UI (client model picker). Using `as const` produces narrow literal
 * types compatible with both `@packages/systems/ai/shared/ChatModel` and
 * `@packages/systems/ai/client/ChatModel` without importing either.
 */
import {VCC_AI_ASSISTANT_MODEL_CONFIG_DEFAULT} from '@packages/systems/ai/shared';
import type {AgentHint} from './constants';

interface ModelSpec {
  readonly provider: string;
  readonly name: string;
}

export interface AgentModelConfig {
  readonly defaultModel: ModelSpec;
  readonly allowedOverrideModels: readonly ModelSpec[];
}

export const DEFAULT_ALLOWED_MODELS = [
  {provider: 'anthropic', name: 'claude-haiku-4-5'},
  {provider: 'anthropic', name: 'claude-sonnet-4'},
  {provider: 'anthropic', name: 'claude-sonnet-4-5'},
  {provider: 'anthropic', name: 'claude-sonnet-4-6'},
  {provider: 'anthropic', name: 'claude-sonnet-5'},
  {provider: 'anthropic', name: 'claude-opus-4-5'},
  {provider: 'anthropic', name: 'claude-opus-4-6'},
  {provider: 'anthropic', name: 'claude-opus-5'},
  {provider: 'anthropic', name: 'claude-opus-5-5'},
  {provider: 'anthropic', name: 'claude-haiku-5-5'},
  {provider: 'openai', name: 'gpt-4.1'},
  {provider: 'openai', name: 'gpt-4.1-mini'},
  {provider: 'openai', name: 'gpt-5'},
  {provider: 'openai', name: 'gpt-5-mini'},
  {provider: 'openai', name: 'gpt-5-nano'},
  {provider: 'openai', name: 'gpt-5.2'},
  {provider: 'openai', name: 'gpt-5.2-pro'},
  {provider: 'openai', name: 'gpt-5.3-chat-latest'},
  {provider: 'openai', name: 'gpt-5.4'},
  {provider: 'openai', name: 'gpt-5.4-pro'},
  {provider: 'openai', name: 'gpt-5.4-mini'},
  {provider: 'openai', name: 'gpt-5.5'},
  {provider: 'openai', name: 'gpt-5.6-luna'},
  {provider: 'openai', name: 'gpt-5.6-terra'},
  {provider: 'openai', name: 'gpt-5.6-sol'},
  {provider: 'openai', name: 'gpt-6-luna'},
  {provider: 'openai', name: 'gpt-6-sol'},
  {provider: 'openai', name: 'gpt-6.1-terra'},
  {provider: 'openai', name: 'gpt-6.1-sol'},
  {provider: 'openai', name: 'chat-latest'},
  {provider: 'google', name: 'gemini-3-flash-preview'},
  {provider: 'google', name: 'gemini-3.6-flash'},
  {provider: 'google', name: 'gemini-3.5-flash'},
  {provider: 'google', name: 'gemini-3.5-flash-lite'},
  {provider: 'google', name: 'gemini-3.7-flash'},
  {provider: 'google', name: 'gemini-3.8-flash'},
  {provider: 'google', name: 'gemini-3.1-pro-preview'},
  {provider: 'google', name: 'gemini-3.1-flash-lite'},
  {provider: 'openrouter', name: 'x-ai/grok-4.5'},
  {provider: 'openrouter', name: 'z-ai/glm-5.2'},
  {provider: 'openrouter', name: 'moonshotai/kimi-k2.6'},
  {provider: 'openrouter', name: 'moonshotai/kimi-k3'},
  {provider: 'openrouter', name: 'xiaomi/mimo-v2.5-pro'},
  {provider: 'openrouter', name: 'deepseek/deepseek-v4-pro'},
  {provider: 'openrouter', name: 'deepseek/deepseek-v4-flash'},
  {provider: 'openrouter', name: 'deepseek/deepseek-v4.1-flash'},
] as const;

export const AIA_DEFAULT_MODEL = {
  provider: 'google',
  name: 'gemini-3.8-flash',
} as const;

/**
 * Static fallback for the Orion/App Gen agent default model.
 * The actual runtime default may differ via dynamic config
 * (`dc-aio-31-app-gen-models`).
 */
export const ORION_DEFAULT_MODEL = {
  provider: 'anthropic',
  name: 'claude-sonnet-4-5',
} as const;

const AGENT_MODEL_CONFIGS: Partial<Record<AgentHint, AgentModelConfig>> = {
  aia: {
    defaultModel: AIA_DEFAULT_MODEL,
    allowedOverrideModels: DEFAULT_ALLOWED_MODELS,
  },
  vcc: {
    defaultModel: VCC_AI_ASSISTANT_MODEL_CONFIG_DEFAULT.defaultModel,
    allowedOverrideModels: DEFAULT_ALLOWED_MODELS,
  },
  orion: {
    defaultModel: ORION_DEFAULT_MODEL,
    allowedOverrideModels: [ORION_DEFAULT_MODEL],
  },
};

const FALLBACK_CONFIG: AgentModelConfig = {
  defaultModel: AIA_DEFAULT_MODEL,
  allowedOverrideModels: DEFAULT_ALLOWED_MODELS,
};

export function getAgentModelConfig(hint: string): AgentModelConfig {
  return AGENT_MODEL_CONFIGS[hint as AgentHint] ?? FALLBACK_CONFIG;
}

export function modelToKey(model: ModelSpec): string {
  return `${model.provider}:${model.name}`;
}

export function keyToModel(key: string): ModelSpec {
  const separatorIndex = key.indexOf(':');
  return {
    provider: key.slice(0, separatorIndex),
    name: key.slice(separatorIndex + 1),
  };
}

export function formatModelLabel(model: ModelSpec): string {
  return `${model.provider} / ${model.name}`;
}
